/**
 * Unified analytics façade. Call `track(event, params, options)` once and the
 * event fans out to every configured platform (Meta Pixel, Google Ads, …).
 *
 * Add a platform: implement `AnalyticsPlatform` in a sibling file, then push
 * it into `platforms` below. Callers don't change.
 */
import { googlePlatform } from "./google";
import { metaPlatform } from "./meta";
import type {
	AnalyticsEvent,
	AnalyticsOptions,
	AnalyticsParams,
	AnalyticsPlatform,
} from "./types";

export type { AnalyticsEvent, AnalyticsOptions, AnalyticsParams } from "./types";

const platforms: AnalyticsPlatform[] = [metaPlatform, googlePlatform];

export function initAnalytics() {
	for (const platform of platforms) {
		try {
			platform.init();
		} catch {
			// platform init failures must not break the app
		}
	}
}

export function track(
	event: AnalyticsEvent,
	params?: AnalyticsParams,
	options?: AnalyticsOptions
) {
	for (const platform of platforms) {
		try {
			platform.track(event, params, options);
		} catch {
			// individual platform failures must not break others
		}
	}
}

// Convenience wrappers — these are the names imported across the app.
export const trackPageView = (params?: AnalyticsParams) =>
	track("page_view", params);

export const trackViewContent = (params?: AnalyticsParams) =>
	track("view_content", params);

export const trackAddToCart = (params?: AnalyticsParams) =>
	track("add_to_cart", params);

export const trackInitiateCheckout = (
	params?: AnalyticsParams,
	options?: AnalyticsOptions
) => track("initiate_checkout", params, options);

export const trackLead = (
	params?: AnalyticsParams,
	options?: AnalyticsOptions
) => track("lead", params, options);

export const trackCompleteRegistration = (
	params?: AnalyticsParams,
	options?: AnalyticsOptions
) => track("complete_registration", params, options);

export const trackPurchase = (
	params?: AnalyticsParams,
	options?: AnalyticsOptions
) => track("purchase", params, options);
